import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, CheckCheck, Wrench, TrendingUp, Calendar, AlertTriangle, Key } from 'lucide-react';
import { notificationsAPI } from '../api';

const NOTIFICATION_ICONS = {
  ot_assigned: Wrench,
  ot_new: Wrench,
  lead_new: TrendingUp,
  essai_reminder: Calendar,
  ot_late: AlertTriangle,
  license_expiring: Key,
  test: Bell,
  connected: Check,
};

const NOTIFICATION_COLORS = {
  ot_assigned: 'var(--blue)',
  ot_new: 'var(--accent)',
  lead_new: 'var(--green)',
  essai_reminder: 'var(--orange)',
  ot_late: 'var(--red)',
  license_expiring: 'var(--yellow)',
  test: 'var(--text3)',
  connected: 'var(--green)',
};

const NotificationItem = ({ notification, onMarkRead }) => {
  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
  const color = NOTIFICATION_COLORS[notification.type] || 'var(--accent)';
  const isUnread = !notification.read;
  
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  return (
    <div 
      className={`p-3 border-b border-[var(--border)] hover:bg-[var(--bg2)] transition-colors cursor-pointer ${isUnread ? 'bg-[var(--bg2)]/50' : ''}`}
      onClick={() => isUnread && onMarkRead(notification.id)}
      data-testid={`notification-${notification.id}`}
    >
      <div className="flex items-start gap-3">
        <div 
          className="p-2 rounded-lg" 
          style={{ background: `${color}20` }}
        >
          <Icon size={16} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isUnread ? 'font-semibold' : ''}`}>{notification.title}</span>
            {isUnread && <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />}
          </div>
          <p className="text-xs text-[var(--text3)] mt-0.5 truncate">{notification.message}</p>
          <span className="text-xs text-[var(--text3)]">{timeAgo(notification.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

const NotificationCenter = ({ token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const eventSourceRef = useRef(null);
  const panelRef = useRef(null);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationsAPI.getAll(50),
        notificationsAPI.getUnreadCount()
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  }, []);

  // Setup SSE connection
  useEffect(() => {
    if (!token) return;

    const connectSSE = () => {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const eventSource = new EventSource(`${baseUrl}/api/notifications/stream`, {
        // Note: SSE doesn't support custom headers, so we use a query param or cookie
        // For now, we'll load notifications via API polling as a fallback
      });

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'connected') {
            console.log('SSE connected');
          } else {
            // New notification received
            setNotifications(prev => [data, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show browser notification if permitted
            if (Notification.permission === 'granted') {
              new Notification(data.title, { body: data.message });
            }
          }
        } catch (err) {
          console.error('Error parsing SSE message:', err);
        }
      };

      eventSource.onerror = (err) => {
        console.error('SSE error:', err);
        eventSource.close();
        // Reconnect after 5 seconds
        setTimeout(connectSSE, 5000);
      };

      eventSourceRef.current = eventSource;
    };

    // For now, use polling since SSE requires token auth handling
    // connectSSE();
    loadNotifications();
    
    // Poll for new notifications every 30 seconds
    const pollInterval = setInterval(loadNotifications, 30000);

    return () => {
      clearInterval(pollInterval);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [token, loadNotifications]);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Will ask permission when user first clicks on notifications
    }
  }, []);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead([notificationId]);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markRead([]);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleRequestPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Notification Bell */}
      <button
        className="relative p-2 rounded-lg hover:bg-[var(--bg2)] transition-colors"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) loadNotifications();
        }}
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-[var(--red)] rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div 
          className="absolute right-0 top-12 w-80 max-h-96 bg-[var(--bg1)] border border-[var(--border)] rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
            <span className="font-semibold">Notifications</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  className="text-xs text-[var(--accent)] hover:underline"
                  onClick={handleMarkAllRead}
                >
                  <CheckCheck size={14} className="inline mr-1" />
                  Tout marquer lu
                </button>
              )}
              <button
                className="p-1 hover:bg-[var(--bg2)] rounded"
                onClick={() => setIsOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-72">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--accent)]" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-[var(--text3)]">
                <Bell size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.slice(0, 10).map(notif => (
                <NotificationItem 
                  key={notif.id} 
                  notification={notif} 
                  onMarkRead={handleMarkRead}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 10 && (
            <div className="p-2 border-t border-[var(--border)] text-center">
              <button className="text-xs text-[var(--accent)] hover:underline">
                Voir toutes les notifications
              </button>
            </div>
          )}

          {/* Browser notification permission */}
          {'Notification' in window && Notification.permission === 'default' && (
            <div className="p-2 border-t border-[var(--border)] bg-[var(--bg2)]">
              <button
                className="text-xs text-[var(--accent)] hover:underline w-full text-center"
                onClick={handleRequestPermission}
              >
                Activer les notifications desktop
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
